/**
 * Das zod-Schema der RuleConfig — Validierung und Defaults an einer Stelle.
 *
 * Die **Typen** liegen in `@flaghex/engine` (`rule-config.ts`), das **Schema** hier. So
 * kennt die Engine zod nicht, und es gibt dennoch nur eine Quelle für die Form der
 * Konfiguration: `parseRuleConfig` gibt das Ergebnis als `RuleConfig` zurück, und der
 * Compiler prüft dabei, dass Schema und Engine-Typ zusammenpassen.
 *
 * Hier leben auch die **Defaults** — also die Balancewerte, die laut `CLAUDE.md` Regel 2
 * nirgends im Code stehen dürfen. Dies ist die Konfigurationsschicht, nicht Spiellogik.
 * Werte und Begründungen: `docs/RULE_CONFIG.md`.
 *
 * Zwei Entscheidungen, die nicht offensichtlich sind:
 *
 * 1. **`.prefault()` statt `.default()`.** In zod 4 wird der Wert eines `.default()`
 *    *nicht* durch das Schema geparst — `.default({})` auf einem verschachtelten Objekt
 *    liefert wörtlich `{}`, die inneren Defaults greifen nicht. `.prefault()` parst den
 *    Wert und füllt damit auch die Tiefe. Mit `.default()` wäre eine Konfiguration ohne
 *    Angaben still halbleer geblieben.
 * 2. **`z.strictObject` überall.** Unbekannte Felder werden abgewiesen, statt ignoriert.
 *    In einem Balancing-Werkzeug ist ein stillschweigend verschlucktes `pushDistance`
 *    statt `push.distance` schlimmer als ein Fehler: die Testpartie liefe mit anderen
 *    Werten als gedacht, und niemand würde es merken.
 */
import { RING_DISTANCE, createBoard, createSectorLayout, startZoneCells, totalPieceCount, } from '@flaghex/engine';
import { z } from 'zod';
const positiveInt = z.number().int().min(1);
const nonNegativeInt = z.number().int().min(0);
/** Reichweite in geraden Hex-Schritten. `max: null` bedeutet unbegrenzt (§16). */
const rangeSchema = z
    .strictObject({
    min: positiveInt,
    max: z.union([positiveInt, z.null()]),
})
    .refine((range) => range.max === null || range.max >= range.min, {
    message: 'Reichweite: min darf nicht größer als max sein',
});
/** Reichweite ohne „unbegrenzt" — für das Stoßen, wo eine Obergrenze zwingend ist. */
const finiteRangeSchema = z
    .strictObject({
    min: positiveInt,
    max: positiveInt,
})
    .refine((range) => range.max >= range.min, {
    message: 'Reichweite: min darf nicht größer als max sein',
});
const movementSchema = z
    .strictObject({
    min: positiveInt,
    max: z.union([positiveInt, z.null()]),
    canJump: z.boolean().optional(),
})
    .refine((range) => range.max === null || range.max >= range.min, {
    message: 'Bewegung: min darf nicht größer als max sein',
});
/**
 * Schlagreichweite. Der Marker `'sameAsMovement'` heißt „so weit wie die Bewegung"
 * (Läufer und Springer) — bewusst kein zweiter Satz Zahlen, damit eine
 * Reichweitenänderung im Balancing nicht die Hälfte verpassen kann.
 */
const captureSchema = z.union([rangeSchema, z.literal('sameAsMovement')]);
const pushSchema = z
    .strictObject({
    range: finiteRangeSchema.prefault({ min: 1, max: 2 }),
    distance: positiveInt.prefault(1),
    pusherAdvance: z.number().int().min(0).prefault(0),
    canPushOffBoard: z.boolean().prefault(true),
    /**
     * Ein Fähnrich auf dem Zielfeld ist stoßbar (`R11`, Nr. 51). **Vorgabe aus**: alte Snapshots
     * und gespeicherte globale Einstellungen kennen das Feld nicht und dürfen sich nicht ändern.
     */
    canPushFlagBearerFromGoal: z.boolean().prefault(false),
})
    .prefault({});
const piecesSchema = z
    .strictObject({
    flagBearer: z
        .strictObject({
        count: positiveInt.prefault(1),
        movement: movementSchema.prefault({ min: 1, max: 3 }),
    })
        .prefault({}),
    guard: z
        .strictObject({
        count: positiveInt.prefault(2),
        movement: movementSchema.prefault({ min: 1, max: 1 }),
        capture: captureSchema.prefault({ min: 1, max: 2 }),
    })
        .prefault({}),
    runner: z
        .strictObject({
        count: positiveInt.prefault(2),
        movement: movementSchema.prefault({ min: 1, max: null }),
        capture: captureSchema.prefault('sameAsMovement'),
    })
        .prefault({}),
    jumper: z
        .strictObject({
        count: positiveInt.prefault(2),
        movement: movementSchema.prefault({ min: 2, max: 3, canJump: true }),
        capture: captureSchema.prefault('sameAsMovement'),
    })
        .prefault({}),
    pusher: z
        .strictObject({
        count: positiveInt.prefault(2),
        movement: movementSchema.prefault({ min: 1, max: 1 }),
        push: pushSchema,
    })
        .prefault({}),
})
    .prefault({});
const baseSchema = z
    .strictObject({
    /** Wird mit jeder Partie gespeichert, damit Balancing-Daten zuordenbar bleiben (§97). */
    version: z.string().min(1).prefault('1'),
    board: z.strictObject({ sideLength: positiveInt.prefault(9) }).prefault({}),
    startZone: z.strictObject({ rows: positiveInt.prefault(2) }).prefault({}),
    scoring: z
        .strictObject({
        target: positiveInt.prefault(5),
        resetScoreOnFlagCapture: z.boolean().prefault(false),
    })
        .prefault({}),
    /**
     * Die Aufstellungszeit (§42, §11). `null` heißt **unbegrenzt** und ist der Default — eine
     * Zahl wäre eine erfundene Balancegröße, und §42 nennt „unbegrenzt" als erste Option.
     *
     * In Millisekunden, wie jede Zeit im Rechenkern. Der Host stellt Minuten ein; umgerechnet
     * wird in der Partieerstellung.
     */
    setup: z
        .strictObject({ timeLimitMs: z.union([positiveInt, z.null()]).prefault(null) })
        .prefault({}),
    respawn: z.strictObject({ enabled: z.boolean().prefault(true) }).prefault({}),
    ring: z.strictObject({ enabled: z.boolean().prefault(true) }).prefault({}),
    /**
     * Verhalten des zentralen Zielfeldes für durchziehende Figuren. §19 regelt nur das
     * Betreten; das Durchqueren ist eine Balancing-Option. Default `'blocked'` (User,
     * 2026-10-02), die beiden anderen Varianten sind zum Durchspielen gedacht.
     */
    goalCell: z
        .strictObject({
        passage: z.enum(['open', 'jumpersOnly', 'blocked']).prefault('blocked'),
    })
        .prefault({}),
    /**
     * Was für **Bots** gilt (`OPEN_QUESTIONS.md` Nr. 45, `RULE_CONFIG.md`).
     *
     * **Die Gruppe heißt `bots`, weil sie sagt, *wen* sie bindet.** Für einen Menschen ist die
     * Erwägungsdauer das, was er braucht; die 300 ms aus **R7** sind eine Sperre gegen eine
     * Doppelklick-Geste und keine Regel.
     *
     * **Eine Nebenwirkung, die zu kennen ist:** durch das `prefault` bekommt ein
     * **bestehender** Snapshot das Feld beim Lesen, obwohl es beim Schreiben nicht darin
     * stand. Regel 3 behandelt den Snapshot als eingefroren — unschädlich ist das nur, weil
     * in keiner bestehenden Partie ein Bot spielt. Bei einem Feld, das menschliches Spiel
     * berührt, wäre es kein zulässiger Weg.
     */
    bots: z
        .strictObject({
        /** 0 ist erlaubt und ausdrücklich gemeint — der Wert für die Massensimulation. */
        minDeliberationMs: nonNegativeInt.prefault(2000),
    })
        .prefault({}),
    pieces: piecesSchema,
})
    .prefault({});
/**
 * Die RuleConfig mit allen Querprüfungen.
 *
 * Diese Prüfungen brauchen Board-Geometrie und benutzen deshalb die Engine. Das ist die
 * erlaubte Richtung: `contracts` darf aus `engine` importieren, nie umgekehrt
 * (`docs/ARCHITECTURE.md`).
 *
 * Ziel ist, dass eine unspielbare Konfiguration **beim Anlegen** scheitert und nicht erst
 * beim ersten Zug mitten in einer Partie.
 */
export const ruleConfigSchema = baseSchema.superRefine((config, ctx) => {
    const radius = config.board.sideLength - 1;
    if (config.startZone.rows > radius) {
        ctx.addIssue({
            code: 'custom',
            path: ['startZone', 'rows'],
            message: `Startreihen: ${config.startZone.rows} übersteigt die Sektortiefe ${radius} ` +
                `bei Kantenlänge ${config.board.sideLength}`,
        });
        // Ohne gültige Reihenzahl sind die folgenden Geometrieprüfungen nicht aussagekräftig.
        return;
    }
    if (config.ring.enabled && radius < RING_DISTANCE) {
        ctx.addIssue({
            code: 'custom',
            path: ['ring', 'enabled'],
            message: `Ring: liegt auf Distanz ${RING_DISTANCE}, das Brett mit Kantenlänge ` +
                `${config.board.sideLength} reicht nur bis Distanz ${radius}`,
        });
    }
    const layout = createSectorLayout(createBoard({ sideLength: config.board.sideLength }));
    const available = startZoneCells(layout, 'green', { rows: config.startZone.rows }).length;
    const needed = totalPieceCount(config);
    if (needed > available) {
        ctx.addIssue({
            code: 'custom',
            path: ['pieces'],
            message: `Startzone: ${needed} Figuren passen nicht auf ${available} Startfelder ` +
                `(${config.startZone.rows} Reihen bei Kantenlänge ${config.board.sideLength})`,
        });
    }
    const push = config.pieces.pusher.push;
    const reachableAdvance = push.range.max + push.distance - 1;
    if (push.pusherAdvance > reachableAdvance) {
        ctx.addIssue({
            code: 'custom',
            path: ['pieces', 'pusher', 'push', 'pusherAdvance'],
            message: `push.pusherAdvance ${push.pusherAdvance} ist nie erreichbar: bei range.max ` +
                `${push.range.max} und distance ${push.distance} sind höchstens ${reachableAdvance} ` +
                `Felder möglich`,
        });
    }
});
/**
 * Validiert eine Konfiguration und füllt alle Defaults auf. Wirft bei ungültiger Eingabe.
 *
 * Die Rückgabe ist als `RuleConfig` typisiert — damit prüft der Compiler bei jedem Build,
 * dass das Schema und der Engine-Typ nicht auseinanderlaufen.
 */
/**
 * Dasselbe Schema als **Antwortvertrag** — mit dem Engine-Typ als Ausgabe.
 *
 * Dritter Fall derselben Sache (nach `SerializedGameState` und der `STATE`-Nachricht): das
 * Schema ist je Figurenklasse **enger** als `RuleConfig` — der Wächter *hat* eine
 * Schlagreichweite, der Fähnrich nicht. Zur Laufzeit passt beides zusammen, im Typsystem nicht,
 * und eine Route, die eine `RuleConfig` zurückgibt, scheitert sonst am Antwortschema.
 *
 * Geprüft wird weiterhin mit demselben Schema; getragen wird der Engine-Typ.
 */
export const ruleConfigResponseSchema = ruleConfigSchema;
export function parseRuleConfig(input) {
    const parsed = ruleConfigSchema.parse(input);
    return parsed;
}
/**
 * Die vollständige Standardkonfiguration — Ausgangspunkt der globalen Settings-Seite und
 * Grundlage des Snapshots, der beim Erstellen einer Partie kopiert wird (§53).
 */
export function defaultRuleConfig() {
    return parseRuleConfig(undefined);
}
//# sourceMappingURL=rule-config.js.map